/* ==========================================================================
   PWR UTILS
   Zero-dependency helpers shared across the PWR JS architecture.
   Loaded FIRST. Registers window.PWR.utils and window.PWR (namespace root).
   No ES module imports (Shopify themes have no default build step); a single
   global namespace is used instead, mirroring Dawn's own approach.
   ========================================================================== */
(function () {
  'use strict';

  // Idempotent namespace bootstrap — safe across Theme Editor re-injects.
  window.PWR = window.PWR || {};
  if (window.PWR.utils) return;

  const BREAKPOINTS = { sm: 750, md: 990, lg: 1400 };

  const utils = {
    BREAKPOINTS,

    /* ---------------------------------------------------------------- DOM */
    qs(selector, scope) {
      return (scope || document).querySelector(selector);
    },

    qsa(selector, scope) {
      return Array.prototype.slice.call((scope || document).querySelectorAll(selector));
    },

    /** Attach a delegated listener; returns an unbind function. */
    on(root, type, selector, handler, options) {
      const listener = (event) => {
        const target = event.target.closest(selector);
        if (target && root.contains(target)) handler(event, target);
      };
      root.addEventListener(type, listener, options);
      return () => root.removeEventListener(type, listener, options);
    },

    /** Run cb now if DOM is ready, else on DOMContentLoaded. */
    ready(cb) {
      if (document.readyState !== 'loading') {
        cb();
      } else {
        document.addEventListener('DOMContentLoaded', cb, { once: true });
      }
    },

    /* --------------------------------------------------------- TIMING */
    debounce(fn, wait) {
      let t;
      const wrapped = function () {
        const args = arguments;
        clearTimeout(t);
        t = setTimeout(() => fn.apply(this, args), wait);
      };
      wrapped.cancel = () => clearTimeout(t);
      return wrapped;
    },

    throttle(fn, limit) {
      let inThrottle = false;
      let lastArgs = null;
      return function () {
        const args = arguments;
        if (inThrottle) {
          lastArgs = args;
          return;
        }
        fn.apply(this, args);
        inThrottle = true;
        setTimeout(() => {
          inThrottle = false;
          if (lastArgs) {
            fn.apply(this, lastArgs);
            lastArgs = null;
          }
        }, limit);
      };
    },

    /** requestAnimationFrame-coalesced callback (for scroll/resize). */
    rafThrottle(fn) {
      let scheduled = false;
      return function () {
        const args = arguments;
        const ctx = this;
        if (scheduled) return;
        scheduled = true;
        window.requestAnimationFrame(() => {
          scheduled = false;
          fn.apply(ctx, args);
        });
      };
    },

    nextFrame() {
      return new Promise((resolve) => window.requestAnimationFrame(() => resolve()));
    },

    /* -------------------------------------------------- MEDIA / MOTION */
    mediaQuery(minWidth) {
      return window.matchMedia(`(min-width: ${minWidth}px)`);
    },

    isMobile() {
      return !this.mediaQuery(BREAKPOINTS.sm).matches;
    },

    isDesktop() {
      return this.mediaQuery(BREAKPOINTS.md).matches;
    },

    prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    },

    supportsHover() {
      return window.matchMedia('(hover: hover)').matches;
    },

    /* ------------------------------------------------------------- HTTP */
    async fetchJSON(url, options) {
      const response = await fetch(url, options);
      const contentType = response.headers.get('content-type') || '';
      // Shopify's legacy .js-suffixed AJAX endpoints (e.g. /cart.js) can be
      // served as application/json or as (text|application)/javascript
      // depending on environment — both are JSON bodies in practice.
      const isJSON = /\b(?:application\/json|(?:text|application)\/javascript)\b/.test(contentType);
      const data = isJSON ? await response.json() : await response.text();
      if (!response.ok) {
        const error = new Error('Request failed');
        error.status = response.status;
        error.data = data;
        throw error;
      }
      return data;
    },

    /** Shopify routes helper with sensible fallbacks. */
    route(name) {
      const routes = window.routes || {};
      const fallbacks = {
        cart_add_url: '/cart/add',
        cart_change_url: '/cart/change',
        cart_update_url: '/cart/update',
        cart_url: '/cart',
        predictive_search_url: '/search/suggest',
      };
      return routes[name] || fallbacks[name] || '';
    },

    /**
     * Refetches the real Shopify cart and writes the current item_count into
     * every [data-pwr-cart-count] badge. Decoupled from Section Rendering API
     * markup entirely, so it can't desync from a section-id mismatch and never
     * touches the icon's own DOM (only the count text + hidden attribute).
     */
    async syncCartCount() {
      let cart;
      try {
        cart = await utils.fetchJSON('/cart.js');
      } catch (e) {
        return null;
      }
      const count = cart.item_count;
      utils.qsa('[data-pwr-cart-count]').forEach((el) => {
        el.textContent = count;
        if (count > 0) {
          el.removeAttribute('hidden');
        } else {
          el.setAttribute('hidden', '');
        }
      });
      return cart;
    },

    /** Money formatter using Shopify's format string when available. */
    formatMoney(cents, format) {
      const value = (cents / 100).toFixed(2);
      const fmt = format || window.PWR?.settings?.moneyFormat || '${{amount}}';
      return fmt.replace(/\{\{\s*amount\s*\}\}/, value);
    },

    /* ---------------------------------------------------------- STORAGE */
    storage: {
      get(key, fallback) {
        try {
          const raw = window.localStorage.getItem(key);
          return raw === null ? fallback : JSON.parse(raw);
        } catch (e) {
          return fallback;
        }
      },
      set(key, value) {
        try {
          window.localStorage.setItem(key, JSON.stringify(value));
          return true;
        } catch (e) {
          return false;
        }
      },
      remove(key) {
        try {
          window.localStorage.removeItem(key);
          return true;
        } catch (e) {
          return false;
        }
      },
    },

    /* -------------------------------------------------- A11Y HELPERS */
    /** Trap focus within an element; returns a release function. */
    trapFocus(container, initialFocus) {
      const FOCUSABLE = [
        'a[href]', 'button:not([disabled])', 'textarea:not([disabled])',
        'input:not([disabled]):not([type="hidden"])', 'select:not([disabled])',
        '[tabindex]:not([tabindex="-1"])',
      ].join(',');

      const getNodes = () => utils.qsa(FOCUSABLE, container).filter((el) => el.offsetParent !== null);

      const onKeydown = (event) => {
        if (event.key !== 'Tab') return;
        const nodes = getNodes();
        if (!nodes.length) return;
        const first = nodes[0];
        const last = nodes[nodes.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      };

      container.addEventListener('keydown', onKeydown);
      (initialFocus || getNodes()[0] || container).focus();
      return () => container.removeEventListener('keydown', onKeydown);
    },

    /** Announce a message to screen readers via a shared live region. */
    announce(message, assertive) {
      let region = document.getElementById('pwr-live-region');
      if (!region) {
        region = document.createElement('p');
        region.id = 'pwr-live-region';
        region.className = 'pwr-visually-hidden';
        region.setAttribute('aria-live', assertive ? 'assertive' : 'polite');
        region.setAttribute('role', 'status');
        document.body.appendChild(region);
      }
      region.setAttribute('aria-live', assertive ? 'assertive' : 'polite');
      // Clear then set on next frame so repeated identical messages re-announce.
      region.textContent = '';
      window.requestAnimationFrame(() => { region.textContent = message; });
    },

    /* -------------------------------------------------------- MISC */
    clamp(value, min, max) {
      return Math.min(Math.max(value, min), max);
    },

    uid(prefix) {
      return (prefix || 'pwr') + '-' + Math.random().toString(36).slice(2, 9);
    },

    parseData(el, attr) {
      const raw = el.getAttribute(attr);
      if (!raw) return null;
      try {
        return JSON.parse(raw);
      } catch (e) {
        return raw;
      }
    },
  };

  window.PWR.utils = utils;
})();
